/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import { get, set } from '@submize/lodash';

const initialState = {
  dirty: false,
  initiated: false,
  name: '',
  enabled: true,
  schedule: {
    type: 'always',
    message: 'Sorry, this form is currently disabled.',
  },
  messages: {
    'string-empty': { value: '' },
  },
  design: {
    colors: {
      submize: {
        50: '#ebf8ff',
        100: '#bee3f8',
        200: '#90cdf4',
        300: '#63b3ed',
        400: '#4299e1',
        500: '#3182ce',
        600: '#2b6cb0',
        700: '#2c5282',
        800: '#2a4365',
        900: '#1A365D',
      },
    },
    size: 'sm',
    containerSize: {
      size: 'md',
      value: '60ch',
    },
    backgroundColor: '#00000003',
    shadow: true,
    borderColor: '#C0C4C9',
    textColor: '#474C52',
    background: {
      borderStyle: 'none',
      borderWidth: '1px',
    },
    fields: {},
    layout: {
      paddingY: '12px',
      paddingX: '10px',
    },
  },
};

const shouldDirty = (action) => (action.type.startsWith('form/') && !['form/setForm', 'form/setTemplate'].includes(action.type))
  || (action.type.startsWith('editor/') && !['editor/setSelected', 'editor/setFields'].includes(action.type))
  || action.type === 'actions/updateActions';

const formSlice = createSlice({
  name: 'form',
  initialState,
  reducers: {
    setTemplate(state, action) {
      state.dirty = false;
      state.initiated = true;
      state.name = action.payload.name;
      state.id = action.payload.id;
      state.design = action.payload.schema?.design ? action.payload.schema.design : initialState.design;
    },
    setForm(state, action) {
      state.dirty = false;
      state.initiated = true;
      state.name = action.payload.name;
      state.enabled = action.payload.enabled ?? true;
      state.schedule = action.payload.schedule ?? { type: 'always', message: 'Sorry, this form is currently disabled.' };
      state.urlpath = action.payload.urlpath;
      state.account = action.payload.account;
      state.id = action.payload.id;
      state.messages = action.payload.messages;
      state.design = action.payload.schema?.design ? action.payload.schema.design : initialState.design;
    },
    setFormName(state, action) {
      state.name = action.payload;
    },
    setFormEnabled(state, action) {
      state.enabled = action.payload;
    },
    setFormSchedule(state, action) {
      state.schedule.type = action.payload;
    },
    setFormScheduleMessage(state, action) {
      state.schedule.message = action.payload;
    },
    setFormMessages(state, action) {
      set(state, 'messages.string-empty', action.payload);
    },
    setColor(state, action) {
      set(state, 'design.colors.submize', action.payload);
    },
    setSize(state, action) {
      set(state, 'design.size', action.payload);
    },
    setBgColor(state, action) {
      set(state, 'design.backgroundColor', action.payload);
    },
    setShadow(state, action) {
      set(state, 'design.shadow', action.payload);
    },
    setBorderColor(state, action) {
      set(state, 'design.borderColor', action.payload);
    },
    setTextColor(state, action) {
      set(state, 'design.textColor', action.payload);
    },
    setContainerSize(state, action) {
      set(state, 'design.containerSize', action.payload);
    },
    setBgStyles(state, action) {
      set(state, 'design.background', {
        ...get(state, 'design.background', {}),
        ...action.payload,
      });
    },
    setFieldStyles(state, action) {
      set(state, 'design.fields', {
        ...get(state, 'design.fields', {}),
        ...action.payload,
      });
    },
    setFieldLabelStyles(state, action) {
      set(state, 'design.fields.label', {
        ...get(state, 'design.fields.label', {}),
        ...action.payload,
      });
    },
    setFieldInputStyles(state, action) {
      set(state, 'design.fields.input', {
        ...get(state, 'design.fields.input', {}),
        ...action.payload,
        hover: {
          ...get(state, 'design.fields.input.hover', {}),
          ...get(action, 'payload.hover', {}),
        },
        focus: {
          ...get(state, 'design.fields.input.focus', {}),
          ...get(action, 'payload.focus', {}),
        },
      });
    },
    setFieldPlaceholderStyles(state, action) {
      set(state, 'design.fields.placeholder', {
        ...get(state, 'design.fields.placeholder', {}),
        ...action.payload,
      });
    },
    setLayoutStyles(state, action) {
      set(state, 'design.layout', {
        ...get(state, 'design.layout', {}),
        ...action.payload,
      });
    },
    setDesign(state, action) {
      set(state, 'design', action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(shouldDirty, (state) => {
      state.dirty = true;
    });
  },
});
export default formSlice.reducer;

export const {
  setTemplate,
  setForm,
  setFormName,
  setFormEnabled,
  setFormSchedule,
  setFormScheduleMessage,
  setFormMessages,
  setColor,
  setContainerSize,
  setSize,
  setBgColor,
  setShadow,
  setBorderColor,
  setTextColor,
  setBgStyles,
  setFieldStyles,
  setFieldLabelStyles,
  setFieldInputStyles,
  setFieldPlaceholderStyles,
  setLayoutStyles,
  setDesign,
} = formSlice.actions;

export const getForm = (state) => state.form;
export const getFormDesign = (state) => state.form.design;
